import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BLACK,
  BORDER,
  CARD_BG,
  GOLD,
  GOLD_DARK,
  GOLD_TINT,
  GREEN,
  INK_BG,
  INK_TEXT,
  SURFACE_2,
  TEXT_FAINT,
  TEXT_MUTED,
} from '../../components/ProposalFlow/theme';
import type { PlanFeatureRow, PricingPlan } from '../../types/pricingPlans';
import { intervalLabel } from '../../utils/plans';
import { SourceIcon } from './SourceIcon';
import { LoopDiagram } from './LoopDiagram';

// Where every CTA on the table points. One place to change.
export const PRICING_CTA_URL = 'https://www.thedesirecompany.com/contact';

interface Props {
  plans: PricingPlan[];
  featureRows: PlanFeatureRow[];
  // Sections that start collapsed; their header row expands them.
  collapsedGroups?: string[];
  highlightPlan?: string | null;
}

const money = (n: number) =>
  `$${n.toLocaleString('en-US', { maximumFractionDigits: n % 1 === 0 ? 0 : 2 })}`;

const priceLabel = (p: PricingPlan) => {
  if (p.monthly_price === null || p.monthly_price === undefined) return 'Contact sales';
  if (Number(p.monthly_price) === 0) return 'Free';
  return `${money(Number(p.monthly_price))}/mo`;
};

// "1 product" for the free tier so the hook reads at a glance.
const productsLabel = (p: PricingPlan) => {
  const n = p.default_max_products;
  if (n === null || n === undefined) return 'Custom';
  if (n === 1) return '1 product/PDP';
  return `Up to ${n.toLocaleString('en-US')} products/PDPs`;
};

// The portal's global CSS makes <svg> display:block, which pins it to the
// cell's left edge regardless of text-align — center it explicitly.
// Shared with ProductionSection so both blocks use the same glyph.
export const Check = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    aria-label="Included"
    style={{ display: 'block', margin: '0 auto' }}
  >
    <path d="M5 12.5l4.5 4.5L19 7.5" stroke={GREEN} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Dash = () => (
  <span style={{ color: TEXT_FAINT }} aria-label="Not included">
    —
  </span>
);

// (i) bubble with hover/focus tooltip for rows that carry `info` copy.
// The bubble is rendered in a portal on document.body with fixed
// positioning from the button's screen rect: the table's scroll wrapper
// clips overflow and every label cell is sticky with its own z-index, so
// an in-cell absolute bubble either gets cut off or painted over by the
// next row. Below the (i), horizontally centred on it (clamped to the
// viewport), arrow centred on the (i) whatever the clamp did.
const TIP_W = 260;
const TIP_GAP = 8;
const INFO_CSS = `
.pc-info { position: relative; display: inline-flex; vertical-align: middle; margin-left: 6px; }
.pc-info__btn {
  width: 16px; height: 16px; border-radius: 50%; border: 1px solid ${TEXT_FAINT};
  background: transparent; color: ${TEXT_MUTED}; font: 600 10px/14px inherit;
  font-family: inherit; cursor: help; padding: 0; display: inline-flex;
  align-items: center; justify-content: center;
}
.pc-info__tip {
  position: fixed; width: ${TIP_W}px; padding: 10px 12px; border-radius: 10px; background: ${INK_BG};
  color: ${INK_TEXT}; font-size: 12.5px; font-weight: 400; line-height: 1.45;
  text-align: left; white-space: normal; box-shadow: 0 6px 20px rgba(0,0,0,0.18);
  z-index: 10000; pointer-events: none;
}
.pc-info__arrow {
  position: absolute; bottom: 100%; width: 0; height: 0;
  border: 6px solid transparent; border-bottom-color: ${INK_BG};
}
`;

const Info = ({ text, id }: { text: string; id: string }) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; arrow: number } | null>(null);

  const place = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const margin = 8;
    // Never leave the table: the app sidebar sits to its left and would
    // paint over the bubble (leftmost rows), and the right edge is the
    // scroll wrapper's edge.
    const table = el.closest('table');
    const bounds = table ? table.getBoundingClientRect() : { left: 0, right: window.innerWidth };
    const minLeft = Math.max(margin, bounds.left + margin);
    const maxLeft = Math.max(minLeft, Math.min(window.innerWidth, bounds.right) - TIP_W - margin);
    const left = Math.min(Math.max(minLeft, cx - TIP_W / 2), maxLeft);
    setPos({ top: r.bottom + TIP_GAP, left, arrow: cx - left - 6 });
  };
  const hide = () => setPos(null);

  // Any scroll or resize moves the anchor: just close.
  useLayoutEffect(() => {
    if (!pos) return undefined;
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [pos]);

  return (
    <span className="pc-info">
      <button
        ref={btnRef}
        type="button"
        className="pc-info__btn"
        aria-describedby={id}
        aria-label="More info"
        onMouseEnter={place}
        onMouseLeave={hide}
        onFocus={place}
        onBlur={hide}
      >
        i
      </button>
      {pos &&
        createPortal(
          <span role="tooltip" id={id} className="pc-info__tip" style={{ top: pos.top, left: pos.left }}>
            <span className="pc-info__arrow" style={{ left: pos.arrow }} />
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
};

// Chevron on a row that owns sub-rows (accordion toggle).
const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{
      display: 'inline-block',
      verticalAlign: 'middle',
      marginRight: 8,
      transition: 'transform 120ms ease',
      transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
    }}
  >
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Plans as columns, features as rows. The first column is sticky so the
// row labels stay visible while a phone swipes through the plan columns.
// A row followed by rows that name it as `parent` is an accordion: its
// sub-rows render indented beneath it while it is expanded.
// The min widths only kick in on narrow screens: at the page's 1100px
// shell the table fits without a horizontal scrollbar.
export function PlanComparisonTable({ plans, featureRows, collapsedGroups = [], highlightPlan }: Props) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Collapsible sections the viewer opened.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const collapsible = (name: string | null) => !!name && collapsedGroups.indexOf(name) !== -1;
  const groupOpen = (name: string | null) => !collapsible(name) || !!openGroups[name as string];
  const toggleGroup = (name: string) => setOpenGroups((prev) => ({ ...prev, [name]: !prev[name] }));
  const toggle = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  const hasChildren = (key: string) => featureRows.some((r) => r.parent === key);
  // A row expands when it owns sub-rows or carries a detail panel.
  const expandable = (row: PlanFeatureRow) => hasChildren(row.key) || !!row.detail;

  const cellBase: React.CSSProperties = {
    padding: '14px 16px',
    borderBottom: `1px solid ${BORDER}`,
    fontSize: 14,
    color: BLACK,
    textAlign: 'center',
    verticalAlign: 'middle',
    minWidth: 120,
  };
  const labelCell: React.CSSProperties = {
    ...cellBase,
    textAlign: 'left',
    position: 'sticky',
    left: 0,
    background: CARD_BG,
    zIndex: 1,
    minWidth: 160,
    // Fixed share so the plan and video tables' columns line up.
    width: '22%',
    fontWeight: 500,
  };
  const highlighted = (p: PricingPlan) => highlightPlan && p.key === highlightPlan;
  // A plan's "coming soon" price line whose label names this group ("Video
  // Autopilot"); the section header then marks it per column. Priced lines
  // stay out of the headers: the plan shows only its monthly total.
  const priceLineFor = (p: PricingPlan, group: string) =>
    (p.price_lines || []).find((l) => l.coming_soon && l.label.toLowerCase() === group.toLowerCase());
  const groupPriced = (group: string | null) =>
    !!group && plans.some((p) => !!priceLineFor(p, group));
  const colStyle = (p: PricingPlan): React.CSSProperties =>
    highlighted(p) ? { background: GOLD_TINT } : {};

  const renderValue = (row: PlanFeatureRow, p: PricingPlan) => {
    if (row.key === 'max_products') return productsLabel(p);
    // The label carries "(up to)"; the cell shows the cadence itself.
    if (row.key === 'max_pdp_score_interval') return intervalLabel(p.max_pdp_score_interval);
    const v = p.features ? p.features[row.key] : undefined;
    if (row.type === 'bool') return v === true ? <Check /> : <Dash />;
    if (v === null || v === undefined || v === '') return <Dash />;
    if (row.type === 'number') return Number(v).toLocaleString('en-US');
    return String(v);
  };

  // Group rows under their group headers, preserving order (virtual rows
  // may sit inside a group too — the Autopilot group holds the plan caps).
  // Sub-rows of a collapsed parent are dropped here.
  const groups: Array<{ name: string | null; rows: PlanFeatureRow[] }> = [];
  featureRows.forEach((row) => {
    if (row.parent && !expanded[row.parent]) return;
    const name = row.group || null;
    const last = groups[groups.length - 1];
    if (last && last.name === name) last.rows.push(row);
    else groups.push({ name, rows: [row] });
  });

  return (
    <div
      style={{
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        border: `1px solid ${BORDER}`,
        borderRadius: 16,
        background: CARD_BG,
      }}
    >
      <style>{INFO_CSS}</style>
      <table
        style={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          width: '100%',
          // Fixed layout: the label column takes its 22% share and the plan
          // columns split the rest evenly, so both tables' columns align.
          tableLayout: 'fixed',
          minWidth: 160 + 120 * plans.length,
        }}
      >
        <thead>
          <tr>
            <th style={{ ...labelCell, background: CARD_BG, borderBottom: `1px solid ${BORDER}` }} />
            {plans.map((p) => (
              <th
                key={p.key}
                style={{
                  ...cellBase,
                  ...colStyle(p),
                  padding: '22px 16px 16px',
                  borderTop: highlighted(p) ? `3px solid ${GOLD}` : '3px solid transparent',
                }}
              >
                {highlighted(p) && (
                  <div
                    style={{
                      display: 'inline-block',
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      color: GOLD_DARK,
                      marginBottom: 6,
                    }}
                  >
                    Recommended
                  </div>
                )}
                <div style={{ fontSize: 16, fontWeight: 700, color: BLACK }}>{p.display_name}</div>
                {p.description && (
                  <div style={{ fontSize: 12.5, color: TEXT_MUTED, marginTop: 6, fontWeight: 400 }}>
                    {p.description}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={labelCell}>Monthly price</td>
            {plans.map((p) => (
              <td key={p.key} style={{ ...cellBase, ...colStyle(p), fontSize: 18, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {priceLabel(p)}
                {/* Add-ons are optional and listed separately (a brand can
                    take one, both or none). */}
                {(p.price_lines || []).some((l) => l.addon && l.amount !== null) && (
                  <div style={{ fontSize: 12, fontWeight: 400, color: TEXT_MUTED, marginTop: 4, whiteSpace: 'normal' }}>
                    Optional add-ons:{' '}
                    {(p.price_lines || [])
                      .filter((l) => l.addon && l.amount !== null)
                      .map((l) => `${l.label} +${money(Number(l.amount))}`)
                      .join(' · ')}
                  </div>
                )}
                {/* Status notes: "PDP Autopilot Beta · Video Autopilot coming soon". */}
                {(p.price_lines || []).some((l) => l.beta || l.coming_soon) && (
                  <div style={{ fontSize: 12, fontWeight: 400, color: TEXT_MUTED, marginTop: 4, whiteSpace: 'normal' }}>
                    {[
                      ...(p.price_lines || []).filter((l) => l.beta && !l.coming_soon).map((l) => `${l.label} Beta`),
                      ...(p.price_lines || []).filter((l) => l.coming_soon).map((l) => `${l.label} coming soon`),
                    ].join(' · ')}
                  </div>
                )}
              </td>
            ))}
          </tr>
          {groups.map((g, gi) => (
            <React.Fragment key={`${g.name || 'core'}-${gi}`}>
              {g.name && (
                <tr>
                  <td
                    colSpan={groupPriced(g.name) ? 1 : plans.length + 1}
                    style={{
                      ...cellBase,
                      textAlign: 'left',
                      background: SURFACE_2,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      color: TEXT_MUTED,
                      padding: '8px 16px',
                      position: 'sticky',
                      left: 0,
                    }}
                  >
                    {collapsible(g.name) ? (
                      <button
                        type="button"
                        onClick={() => toggleGroup(g.name as string)}
                        aria-expanded={groupOpen(g.name)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                          letterSpacing: 'inherit',
                          textTransform: 'inherit',
                          color: 'inherit',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <Chevron open={groupOpen(g.name)} />
                        {g.name}
                      </button>
                    ) : (
                      g.name
                    )}
                  </td>
                  {groupPriced(g.name) &&
                    plans.map((p) => {
                      const line = priceLineFor(p, g.name as string);
                      return (
                        <td
                          key={p.key}
                          style={{
                            ...cellBase,
                            ...colStyle(p),
                            background: highlighted(p) ? GOLD_TINT : SURFACE_2,
                            padding: '8px 16px',
                            fontSize: 12.5,
                            fontWeight: 700,
                            color: line ? BLACK : TEXT_MUTED,
                          }}
                        >
                          {line ? 'Coming soon' : ''}
                        </td>
                      );
                    })}
                </tr>
              )}
              {(groupOpen(g.name) ? g.rows : []).map((row) => (
                <React.Fragment key={row.key}>
                <tr>
                  <td
                    className="pc-label"
                    style={
                      row.parent
                        ? { ...labelCell, paddingLeft: 40, fontWeight: 400, color: TEXT_MUTED, fontSize: 13.5 }
                        : labelCell
                    }
                  >
                    {expandable(row) ? (
                      <button
                        type="button"
                        onClick={() => toggle(row.key)}
                        aria-expanded={!!expanded[row.key]}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                          color: 'inherit',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <Chevron open={!!expanded[row.key]} />
                        {row.label}
                      </button>
                    ) : (
                      <>
                        <SourceIcon name={row.icon} />
                        {row.label}
                      </>
                    )}
                    {row.info && <Info text={row.info} id={`pc-info-${row.key}`} />}
                    {row.note && (
                      <div style={{ fontSize: 11.5, color: TEXT_MUTED, fontWeight: 400, marginTop: 2 }}>{row.note}</div>
                    )}
                  </td>
                  {plans.map((p) => (
                    <td key={p.key} style={{ ...cellBase, ...colStyle(p) }}>
                      {renderValue(row, p)}
                    </td>
                  ))}
                </tr>
                {row.detail && expanded[row.key] && (
                  <tr>
                    <td colSpan={plans.length + 1} style={{ ...cellBase, textAlign: 'left', padding: '10px 16px 12px 40px' }}>
                      {row.detail.kind === 'loop' && <LoopDiagram steps={row.detail.steps} />}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
          <tr>
            <td style={{ ...labelCell, borderBottom: 'none' }} />
            {plans.map((p) => (
              <td key={p.key} style={{ ...cellBase, ...colStyle(p), borderBottom: 'none', padding: '18px 16px 22px' }}>
                <a
                  href={PRICING_CTA_URL}
                  target="_top"
                  rel="noreferrer"
                  style={{
                    display: 'inline-block',
                    background: highlighted(p) ? GOLD_DARK : INK_BG,
                    color: INK_TEXT,
                    borderRadius: 10,
                    padding: '10px 18px',
                    fontSize: 13.5,
                    fontWeight: 600,
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.monthly_price === null || p.monthly_price === undefined ? 'Contact sales' : 'Get started'}
                </a>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
